import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { Functionality } from "src/app/shared/constants/functionality.constants";
import { ApiService } from "src/app/shared/services/api.service";
import { PermissionService } from "src/app/shared/services/permission.service";
import {
  IMetadataOptions,
  ISampleMetadata,
  ISampleMetadataModificationDetails,
  ISkippedAndDuplicates,
} from "../interfaces/metadata.interface";

@Injectable({
  providedIn: "root",
})
export class MetadataService {
  constructor(
    private api: ApiService,
    private permissionService: PermissionService
  ) {}

  /**
   * Whether the user's role allows uploading samples metadata (see uploadSampleMetadata()).
   * The backend grants every upload (metadata, RNA-seq, mutations) through the single "/upload" route.
   * @return True if uploading metadata is allowed, false otherwise
   */
  public canUploadSampleMetadata(): boolean {
    return this.permissionService.isRouteEnabled(Functionality.IMPORT_DATA, "POST", "/upload");
  }

  /**
   * Upload samples metadata.
   * @param formData The formdata containing the metdata
   * @return The sampleId slipped and the duplicates ones
   */
  public uploadSampleMetadata(formData: FormData): Observable<ISkippedAndDuplicates> {
    return this.api.post(`upload/metadata`, formData) as Observable<ISkippedAndDuplicates>;
  }

  /**
   * Fetch a given sample metadata by it's postgreSQL id.
   * @param postgreSQLId The postgreSQL sample id to recover
   * @return The sample metadata itself
   */
  public getSampleMetadata(postgreSQLId: number): Observable<ISampleMetadata> {
    return this.api.get(`metadata/${encodeURIComponent(postgreSQLId)}`) as Observable<ISampleMetadata>;
  }

  /**
   * Update a sample metadata.
   * @param postgreSQLId The postgreSQL sample id
   * @param sample Updated metadata
   * @return The updated sample metadata
   */
  public updateSampleMetadata(postgreSQLId: number, sample: ISampleMetadataModificationDetails): Observable<ISampleMetadata> {
    return this.api.patch(`metadata/${encodeURIComponent(postgreSQLId)}`, sample) as Observable<ISampleMetadata>;
  }

  /**
   * Fetch the allowed values of the metadata fields constrained to a fixed value set, to offer them as dropdowns.
   * @return The allowed values of each constrained field
   */
  public getMetadataOptions(): Observable<IMetadataOptions> {
    return this.api.get(`metadata/options`) as Observable<IMetadataOptions>;
  }
}
